import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { clearSentryIdentity, syncSentryIdentity } from "@/lib/sentry-user";
import { clearStaleChunkMarker, recoverFromStaleChunk } from "@/lib/chunk-recovery";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function isBrowserWebSocketBlockedError(error: Error): boolean {
  const message = `${error.name}: ${error.message}`.toLowerCase();
  return (
    message.includes("websocket not available") ||
    (message.includes("websocket") && message.includes("operation is insecure")) ||
    message.includes("insecure websocket")
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const [copied, setCopied] = useState(false);
  const webSocketBlocked = isBrowserWebSocketBlockedError(error);

  // A stale cached chunk after a deploy is the most common cause of this screen;
  // one cache-busting reload fixes it before the user ever reads the copy.
  const recovering = recoverFromStaleChunk(error);

  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  const details = [
    `${error.name}: ${error.message}`,
    typeof window !== "undefined" ? `URL: ${window.location.href}` : "",
    typeof navigator !== "undefined" ? `UA: ${navigator.userAgent}` : "",
    error.stack ? `\n${error.stack}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  if (recovering) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          {webSocketBlocked ? "Live updates were blocked" : "This page didn't load"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {webSocketBlocked
            ? "Your browser or network blocked the live-update connection. Refreshing should keep the rest of the app available."
            : "Something went wrong in your browser. You can try refreshing or head back home."}
        </p>
        <p className="mt-4 break-words rounded-md border border-input bg-muted/40 px-3 py-2 text-left text-xs font-mono text-muted-foreground">
          {error.name}: {error.message || "Unknown error"}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <button
            onClick={() => {
              void navigator.clipboard
                ?.writeText(details)
                .then(() => setCopied(true))
                .catch(() => setCopied(false));
            }}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {copied ? "Details copied" : "Copy details"}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "MyPriorities — Run your business. Not your software." },
      {
        name: "description",
        content:
          "One simple workspace for priorities, tasks, projects, CRM, contracts, revenue and performance reporting.",
      },

      {
        name: "google-site-verification",
        content: "yU0m4-5kEDLVCMY1yoY7n97-9UXtoVJOBETuCWNKT0Y",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap",
      },
      { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en" className="dark">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function AuthSync() {
  const router = useRouter();
  const queryClient = useQueryClient();

  useEffect(() => {
    // The app rendered, so any earlier stale-chunk reload attempt is resolved.
    clearStaleChunkMarker();
    // Attach the signed-in user + account context to Sentry events.
    void syncSentryIdentity();
    let subscription: { unsubscribe: () => void } | undefined;
    try {
      subscription = supabase.auth.onAuthStateChange(handleAuthEvent).data.subscription;
    } catch (error) {
      // Storage-restricted browsers can refuse the auth listener; the app must
      // still render its sign-in form rather than fall to the error screen.
      console.error(error);
    }
    return () => subscription?.unsubscribe();

    function handleAuthEvent(event: string) {
      if (event === "SIGNED_OUT") clearSentryIdentity();
      else if (event === "SIGNED_IN" || event === "USER_UPDATED" || event === "INITIAL_SESSION") {
        window.setTimeout(() => void syncSentryIdentity(), 0);
      }
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      // Supabase invokes this callback while its internal auth lock is held.
      // Deferring prevents route guards from calling auth again re-entrantly,
      // which otherwise leaves Safari and Firefox stuck after a valid login.
      window.setTimeout(() => {
        void router.invalidate();
        if (event !== "SIGNED_OUT") void queryClient.invalidateQueries();
      }, 0);
    }
  }, [router, queryClient]);

  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthSync />
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Toaster />
    </QueryClientProvider>
  );
}
